import { useState, useEffect, useCallback } from 'react'
import { Spin, Alert, message, Typography } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import PannelloIntegrazioneEsterna, { CampoIntegrazione } from '@/components/PannelloIntegrazioneEsterna'
import { invalidateIntegrationSettingsCache } from '@/hooks/useIntegrationSettings'

const { Text } = Typography

interface SettingItem {
  chiave: string
  valore: string | null
  descrizione: string | null
  gruppo: string
  tipo: 'string' | 'secret' | 'url' | 'boolean'
}

type SettingsMap = Record<string, SettingItem[]>

interface PannelloConfig {
  titolo: string
  descrizione?: string
  url?: string
  urlLabel?: string
  chiavi: string[]
  extraProps?: Partial<CampoIntegrazione>[]
}

const PANNELLI: PannelloConfig[] = [
  {
    titolo: 'Infocar Repair',
    descrizione: "Software gestione riparazioni. L'URL configurato viene usato per aprire la scheda veicolo/cliente direttamente dalla modale sinistro in Accettazione e dalle schede veicolo/cliente.",
    url: 'https://www.infocar.it',
    urlLabel: 'Sito Infocar',
    chiavi: ['infocar_repair_url', 'infocar_repair_token'],
    extraProps: [
      { placeholder: 'https://repair.infocar.it' },
      { placeholder: 'Token API (opzionale)', copiabile: false },
    ],
  },
  {
    titolo: 'openapi.it — Lookup Targa',
    descrizione: 'Fornisce dati tecnici del veicolo a partire dalla targa italiana. Usato nel form veicolo per pre-compilare i campi.',
    url: 'https://openapi.it',
    urlLabel: 'openapi.it dashboard',
    chiavi: ['openapi_it_token', 'openapi_it_url'],
    extraProps: [
      { placeholder: 'Bearer xxxxxxxxxxxxxxxx', copiabile: false },
      { placeholder: 'https://api.openapi.it/automotive/IT-car' },
    ],
  },
  {
    titolo: 'WhatsApp Business',
    descrizione: 'Invio messaggi WhatsApp ai clienti tramite webhook esterno.',
    chiavi: ['whatsapp_webhook_url', 'whatsapp_api_token'],
    extraProps: [
      { placeholder: 'https://...' },
      { placeholder: 'Token API WhatsApp', copiabile: false },
    ],
  },
]

const IntegrazioniSettings = () => {
  const [settingsMap, setSettingsMap] = useState<SettingsMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<Record<string, boolean>>({})

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await axiosInstance.get<SettingsMap>(API_ENDPOINTS.SETTINGS)
      setSettingsMap(res.data)
    } catch {
      setError('Impossibile caricare le impostazioni')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleSave = async (chiave: string, valore: string | null) => {
    setSaving((prev) => ({ ...prev, [chiave]: true }))
    try {
      await axiosInstance.put(API_ENDPOINTS.SETTING_BY_KEY(chiave), { valore })
      invalidateIntegrationSettingsCache()
      message.success('Impostazione salvata')
      setSettingsMap((prev) => {
        const next = { ...prev }
        for (const gruppo of Object.keys(next)) {
          next[gruppo] = next[gruppo].map((s) =>
            s.chiave === chiave ? { ...s, valore } : s
          )
        }
        return next
      })
    } catch {
      message.error('Errore durante il salvataggio')
    } finally {
      setSaving((prev) => ({ ...prev, [chiave]: false }))
    }
  }

  const buildCampi = (pannello: PannelloConfig): CampoIntegrazione[] => {
    const allItems = Object.values(settingsMap).flat()
    return pannello.chiavi.map((chiave, idx) => {
      const item = allItems.find((s) => s.chiave === chiave)
      const extra = pannello.extraProps?.[idx] ?? {}
      return {
        etichetta: item?.descrizione ?? chiave,
        chiave,
        tipo: item?.tipo ?? 'string',
        valore: item?.valore ?? null,
        ...extra,
      }
    })
  }

  if (loading) return <Spin style={{ display: 'block', margin: '24px auto' }} />
  if (error) return <Alert type="error" message={error} style={{ margin: 16 }} />

  return (
    <div style={{ paddingTop: 16, maxWidth: 680 }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
        Configura gli URL e le credenziali per i servizi esterni. I valori vengono usati nell'applicazione per aprire schede e pre-compilare dati.
      </Text>
      {PANNELLI.map((p) => (
        <PannelloIntegrazioneEsterna
          key={p.titolo}
          titolo={p.titolo}
          descrizione={p.descrizione}
          url={p.url}
          urlLabel={p.urlLabel}
          campi={buildCampi(p)}
          saving={saving}
          onSave={handleSave}
        />
      ))}
    </div>
  )
}

export default IntegrazioniSettings
